import { HttpProvider } from "../http/http.provider";
import { IAppConfig } from "../http/types/IApplicationConfig";

import { addProvider, IProviderDescriptor, ServiceEndpointConstants } from "../ServiceUtil";
import { Observable } from "rxjs";
import { share, map } from "rxjs/operators";
import
{
    IProjectCodeStatus,
    IProjectCodeRate,
    IProject,
    INewProject,
    IProjectCodeResponse,
    INewProjectContractDocument,
    IProjectContractDocument
} from "./project.interface";
import { IHttpRequestConfig } from "../http";
import { createFormData } from "../util/utilities";

/**
 * 
 */

export class ProjectDelegate
{

    constructor(private SERVICE_CONFIG: IAppConfig, private http: HttpProvider)
    {

    }

    public getAllProjectCodes(orgId: string): Observable<IProjectCodeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.PROJECTS.GET_PROJECT_CODE, null, config).pipe(
            map((response: any) =>
            {
                return {
                    projectCodes: ProjectDelegate.normalizeProjectCodes(response.data.projectCodes),
                    success: response.data.success
                };
            }),
            share());
    }


    public getAllNewProjectCodes(orgId: string): Observable<IProjectCodeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.PROJECTS.GET_NEW_PROJECT_CODE, null, config).pipe(
            map((response: any) =>
            {
                return {
                    projectCodes: ProjectDelegate.normalizeProjectCodes(response.data.projectCodes),
                    success: response.data.success
                };
            }),
            share());
    }
    public getProjectCodeById(projectCodeId: string): Observable<IProjectCodeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                ProjectCodeId: projectCodeId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.PROJECTS.GET_PROJECT_CODE_BY_ID, null, config).pipe(
            map((response: any) =>
            {
                return {
                    projectCodeId: projectCodeId,
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.projectCode),
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewProjectCode(project: INewProject): Observable<IProjectCodeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.CREATE_PROJECT_CODE, project).pipe(
            map((response: any) =>
            {
                return {
                    projectCodes: ProjectDelegate.normalizeProjectCodes(response.data.projectCodes),
                    projectCodeId: response.data.projectCodeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateProjectCode(project: IProject): Observable<IProjectCodeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.UPDATE_PROJECT_CODE, project).pipe(
            map((response: any) =>
            {
                return {
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.updatedProjectCode),
                    projectCodeId: response.data.projectCodeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createProjectCideContracts(documents: INewProjectContractDocument): Observable<IProjectCodeResponse>
    {
        const formdata = createFormData(documents);

        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.SAVE_PROJECT_CONTRACT_DOCUMENT, formdata).pipe(
            map((response: any) =>
            {
                return {
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.updatedProjectCode),
                    projectCodeId: response.data.projectCodeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createProjectCodeRate(projectCodeRate: IProjectCodeRate): Observable<IProjectCodeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.CREATE_PROJECT_CODE_RATE, projectCodeRate).pipe(
            map((response: any) =>
            {
                return {
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.updatedProjectCode),
                    projectCodeId: response.data.projectCodeId,
                    projectCodeRateId: response.data.projectCodeRateId,
                    success: response.data.success
                };
            }),
            share());
    }


    public updateProjectCodeRate(projectCodeRate: IProjectCodeRate): Observable<IProjectCodeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.UPDATE_PROJECT_CODE_RATE, projectCodeRate).pipe(
            map((response: any) =>
            {
                return {
                    projectCodeRateId: projectCodeRate.projectCodeRateId,
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.updatedProjectCode),
                    projectCodeId : response.data.projectCodeId,
                    success: response.data.success
                };
            }),
            share());
    }


    public createProjectCodeStatus(projectCodeStatus: IProjectCodeStatus): Observable<IProjectCodeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.PROJECTS.CREATE_PROJECT_CODE_STATUS, projectCodeStatus).pipe(
            map((response: any) =>
            {
                return {
                    projectCode: ProjectDelegate.normalizeProjectCode(response.data.updatedProjectCode),
                    projectCodeId: response.data.projectCodeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public static normalizeProjectCodes(projectCodes: any): IProject[]
    {
        return projectCodes.map(projectCode =>
        {
            return ProjectDelegate.normalizeProjectCode(projectCode);
        });
    }

    private static normalizeProjectCode(projectCode: any): IProject
    {
        projectCode.createdOn = projectCode.createdToString;
        projectCode.technologyId = projectCode.technology ? projectCode.technology.configurationId : "";
        projectCode.vendorContactName = projectCode.vendorContactName;
        projectCode.clientAddressString = projectCode.clientAddressString;
        projectCode.vendorName = projectCode.vendor ? projectCode.vendor.vendorName : projectCode.vendorName;
        projectCode.clientName = projectCode.client? projectCode.client.clientName : projectCode.clientName;
        projectCode.vendor = projectCode.vendor ? projectCode.vendor : projectCode.vendorName;
        projectCode.employeeFullName = projectCode.employee ? projectCode.employee.employeeFullName : projectCode.employeeName;
        projectCode.recruiterName = projectCode.recruitor ? projectCode.recruitor.RecruitorFullName : "";
        projectCode.projectCodeContractDocuments = ProjectDelegate.normalizeProjectDocuments(projectCode.projectCodeContractDocuments ? projectCode.projectCodeContractDocuments : []);
        const projectCodeRateActive = projectCode.projectCodeRates ? projectCode.projectCodeRates.find(rate => rate.status === true) : null;
        if (projectCodeRateActive)
        {
            projectCode.startDate = projectCode.startDate ? projectCode.startDate : projectCodeRateActive.startDateToString;
            projectCode.endDate = projectCode.endDate ? projectCode.endDate : projectCodeRateActive.endDateToString;
            projectCode.rate = projectCodeRateActive.rate;
        }
        delete projectCode.client;
        delete projectCode.clientAddress;
        delete projectCode.technology;
        delete projectCode.vendorContact;
        delete projectCode.employee;
        delete projectCode.recruitor;

        return projectCode;
    }
    private static normalizeProjectDocuments(docs: any): IProjectContractDocument[]
    {
        return docs.map(doc =>
        {
            return {
                projectCodeContractDocumentId: doc.projectCodeContractDocumentId,
                projectCodeContractDocumentTypeId: doc.projectCodeContractDocumentTypeId,
                projectContractDocumentTypeValue: doc.projectCodeContractDocumentType ? doc.projectCodeContractDocumentType.configurationValue : "",
                fileLocationPath: doc.fileLocationPath,
                lastModifyBy: doc.lastModifyBy,
                lastModifyDateTime: doc.lastModifyDateTime,
                fileName: doc.fileName
            };
        });
    }
}